import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Security,
  Warning,
  Error,
  Info,
  CheckCircle,
  Visibility,
  Edit,
  Refresh,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../App';

const SecurityAlerts = () => {
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [stats, setStats] = useState({});

  const fetchSecurityAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const [alertsRes, unrevAlerts] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/payment-security-alerts`),
        axios.get(`${API_BASE_URL}/api/payment-security-alerts?reviewed=false`)
      ]);

      setAlerts(alertsRes.data);

      // Calculate stats
      const highSeverity = alertsRes.data.filter(a => a.severity === 'HIGH').length;
      const mediumSeverity = alertsRes.data.filter(a => a.severity === 'MEDIUM').length;
      const unreviewed = unrevAlerts.data.length;

      setStats({
        total: alertsRes.data.length,
        unreviewed,
        high: highSeverity,
        medium: mediumSeverity,
        low: alertsRes.data.length - highSeverity - mediumSeverity,
      });
    } catch (error) {
      console.error('Security alerts fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSecurityAlerts();
  }, [fetchSecurityAlerts]);

  const handleViewDetails = (alert) => {
    setSelectedAlert(alert);
    setDetailsOpen(true);
  };

  const handleStartReview = (alert) => {
    setSelectedAlert(alert);
    setReviewNotes('');
    setReviewOpen(true);
  };

  const handleMarkReviewed = async () => {
    try {
      await axios.post(
        `${API_BASE_URL}/api/payment-security-alerts/${selectedAlert.id}/review`,
        {
          reviewedBy: 'admin',
          notes: reviewNotes,
        }
      );

      setReviewOpen(false);
      fetchSecurityAlerts(); // Refresh data
    } catch (error) {
      console.error('Review error:', error);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'HIGH': return 'error';
      case 'MEDIUM': return 'warning';
      default: return 'info';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'HIGH': return <Error color="error" />;
      case 'MEDIUM': return <Warning color="warning" />;
      default: return <Info color="info" />;
    }
  };

  const columns = [
    {
      field: 'paymentRef',
      headerName: 'Payment Ref',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace">
          {params.value?.substring(0, 12)}...
        </Typography>
      ),
    },
    {
      field: 'severity',
      headerName: 'Severity',
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getSeverityColor(params.value)}
          size="small"
          icon={getSeverityIcon(params.value)}
        />
      ),
    },
    {
      field: 'alertType',
      headerName: 'Alert Type',
      flex: 0.8,
      minWidth: 140,
      renderCell: (params) => (
        <Chip
          label={params.value?.replace(/_/g, ' ')}
          variant="outlined"
          size="small"
        />
      ),
    },
    {
      field: 'paymentAmount',
      headerName: 'Amount',
      flex: 0.5,
      minWidth: 100,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          ₦{((params.value || 0) / 100).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'customerEmail',
      headerName: 'Customer Email',
      flex: 1.2,
      minWidth: 180,
    },
    {
      field: 'timestamp',
      headerName: 'Created',
      flex: 0.8,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'reviewed',
      headerName: 'Status',
      flex: 0.5,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Reviewed' : 'Pending'}
          color={params.value ? 'success' : 'warning'}
          size="small"
          icon={params.value ? <CheckCircle /> : <Warning />}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.6,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handleViewDetails(params.row)}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
          {!params.row.reviewed && (
            <Tooltip title="Mark as Reviewed">
              <IconButton
                size="small"
                onClick={() => handleStartReview(params.row)}
              >
                <Edit />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  const StatCard = ({ title, value, color, icon, subtitle }) => (
    <Card elevation={2}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" component="div" color={color}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box color={`${color}.main`}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          Security Alerts
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchSecurityAlerts}
        >
          Refresh
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="Total Alerts"
            value={stats.total}
            color="primary.main"
            icon={<Security sx={{ fontSize: 40 }} />}
            subtitle="All time"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="Unreviewed"
            value={stats.unreviewed}
            color={stats.unreviewed > 0 ? "error.main" : "success.main"}
            icon={<Warning sx={{ fontSize: 40 }} />}
            subtitle="Need attention"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="High Severity"
            value={stats.high}
            color="error.main"
            icon={<Error sx={{ fontSize: 40 }} />}
            subtitle="Critical issues"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="Medium Severity"
            value={stats.medium}
            color="warning.main"
            icon={<Warning sx={{ fontSize: 40 }} />}
            subtitle="Moderate issues"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="Low Severity"
            value={stats.low}
            color="info.main"
            icon={<Info sx={{ fontSize: 40 }} />}
            subtitle="Minor issues"
          />
        </Grid>
      </Grid>

      {/* Unreviewed Alerts Warning */}
      {stats.unreviewed > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="h6">
            {stats.unreviewed} unreviewed security alerts require attention
          </Typography>
          <Typography variant="body2">
            These alerts may indicate potential security issues or payment anomalies.
          </Typography>
        </Alert>
      )}

      {/* Data Grid */}
      <Paper elevation={2} sx={{
        height: { xs: 400, sm: 500, md: 600 },
        width: '100%',
        '& .MuiDataGrid-root': {
          border: 'none',
        }
      }}>
        <DataGrid
          rows={alerts}
          columns={columns}
          pageSize={25}
          rowsPerPageOptions={[10, 25, 50]}
          loading={loading}
          disableSelectionOnClick
          getRowClassName={(params) =>
            !params.row.reviewed ? 'unreviewed-row' : ''
          }
          sx={{
            '& .unreviewed-row': {
              backgroundColor: 'rgba(255, 152, 0, 0.08)',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(224, 224, 224, 1)',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        />
      </Paper>

      {/* Alert Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Security Alert Details
        </DialogTitle>
        <DialogContent>
          {selectedAlert && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Alert Type
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedAlert.alertType?.replace(/_/g, ' ')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Severity
                  </Typography>
                  <Chip
                    label={selectedAlert.severity}
                    color={getSeverityColor(selectedAlert.severity)}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Description
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedAlert.description}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Payment Reference
                  </Typography>
                  <Typography variant="body1" fontFamily="monospace" gutterBottom>
                    {selectedAlert.paymentRef}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Amount
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    ₦{((selectedAlert.paymentAmount || 0) / 100).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Customer Email
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedAlert.customerEmail}
                  </Typography>
                </Grid>
                {selectedAlert.reviewed && (
                  <Grid item xs={12}>
                    <Alert severity="success">
                      <Typography variant="subtitle2">Review Information</Typography>
                      <Typography variant="body2">
                        Reviewed by: {selectedAlert.reviewedBy}<br/>
                        Date: {new Date(selectedAlert.reviewedAt).toLocaleString()}<br/>
                        Notes: {selectedAlert.reviewNotes || 'No notes provided'}
                      </Typography>
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedAlert && !selectedAlert.reviewed && (
            <Button
              onClick={() => {
                setDetailsOpen(false);
                handleStartReview(selectedAlert);
              }}
              variant="contained"
            >
              Mark as Reviewed
            </Button>
          )}
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Review Dialog */}
      <Dialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Mark Alert as Reviewed
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Please provide review notes for this security alert.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Review Notes"
            fullWidth
            multiline
            rows={4}
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            placeholder="Describe the actions taken or reason for marking as reviewed..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewOpen(false)}>Cancel</Button>
          <Button
            onClick={handleMarkReviewed}
            variant="contained"
            disabled={!reviewNotes.trim()}
          >
            Mark as Reviewed
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SecurityAlerts;
